import {
  Query,
  QueryResourceGroupsArgs,
  ResourceGroupWhereInput,
} from '../../__generated__/graphql';
import { useCallback, useEffect, useRef, useState } from 'react';

import { EntityEnum } from '../../@types/client/DashboardUserRolePermissions';
import GET_RESOURCE_GROUPS from '../../graphql/getResourceGroups';
import GET_RESOURCE_GROUP_COUNT from '../../graphql/aggregateResourceGroup';
import OrderByParams from '../../@types/client/OrderByParams';
import RESOURCE_GROUP_SEARCH_FIELDS from './constants/ResourceGroupsSearchFields';
import RESOURCE_GROUP_TABLE_HEADERS from './constants/ResourceGroupsTableHeaders';
import { RootState } from '../../redux/reducers';
import SearchField from '../../@types/SearchField';
import TableHookData from '../@types/TableHookData';
import TableHookProps from '../@types/TableHookProps';
import generateMembershipQuery from '../../util/api/generateMembershipQuery';
import generateResourceGroupsTableData from './functions/generateResourceGroupsTableData';
import parseOrderByDataTableParams from '../../components/DataTable/util/parseOrderByDataTableParams';
import parseResourceGroupsQueryParameters from './util/parseResourceGroupsQueryParameters';
import useGetMany from '../useGetMany';
import useRemoveResourceGroup from '../useRemoveResourceGroup';
import useResourceGroupFilters from './hooks/useResourceGroupFilters';
import { useSelector } from 'react-redux';

type ResourceGroupsQueryParams = {
  orderBy?: OrderByParams;
  where: Partial<ResourceGroupWhereInput>;
};

type GetResourceGroupsData = Pick<Query, 'resourceGroups'>;
type AggregateResourceGroupsQueryData = Pick<Query, 'aggregateResourceGroup'>;

export default function useResourceGroupsTableData({
  dataTableKey = 'resourceGroups',
  formName = 'resourceGroup',
  headers = RESOURCE_GROUP_TABLE_HEADERS,
  isSearchable = true,
  queryOptions,
  searchFields = RESOURCE_GROUP_SEARCH_FIELDS,
}: TableHookProps<QueryResourceGroupsArgs, ResourceGroupWhereInput>): TableHookData<
  ResourceGroupWhereInput
> {
  const membership = useSelector((state: RootState) => state.me.activeMembership);
  const options = useRef<Partial<QueryResourceGroupsArgs>>(queryOptions ?? {});
  const fieldsFiltered = useSelector((state: RootState) => state.dataTable.filters);
  const sortOptions = useSelector((state: RootState) => state.dataTable.sortOptions);
  const filters = useResourceGroupFilters();

  const [searchParameters, setSearchParameters] = useState<Partial<ResourceGroupWhereInput>>({});

  const { data, error, loading, offset, query, setOffset } = useGetMany<
    GetResourceGroupsData,
    QueryResourceGroupsArgs
  >({
    graphqlQuery: GET_RESOURCE_GROUPS,
  });

  const {
    data: countData,
    error: countError,
    loading: countLoading,
    query: countQuery,
  } = useGetMany<AggregateResourceGroupsQueryData, QueryResourceGroupsArgs>({
    graphqlQuery: GET_RESOURCE_GROUP_COUNT,
  });

  const createParams = useCallback((): ResourceGroupsQueryParams => {
    const whereArgs = parseResourceGroupsQueryParameters(fieldsFiltered[dataTableKey]);
    const sortParams = parseOrderByDataTableParams(sortOptions[dataTableKey]);

    return {
      ...(sortParams && { orderBy: sortParams }),
      where: {
        ...generateMembershipQuery(membership),
        ...options.current?.where,
        ...searchParameters,
        ...whereArgs,
      },
    };
  }, [membership, options, searchParameters, fieldsFiltered, sortOptions, dataTableKey]);

  const get = useCallback(() => {
    const params = createParams();

    query({
      ...options.current,
      ...(params.orderBy && { orderBy: params.orderBy }),
      skip: options.current?.skip ?? offset,
      take: options.current?.take ?? 10,
      where: params.where,
    });
  }, [createParams, offset, query]);

  const getCount = useCallback(() => {
    const params = createParams();

    countQuery({
      where: params.where,
    });
  }, [countQuery, createParams]);

  useEffect(() => {
    get();
  }, [get]);

  useEffect(() => {
    getCount();
  }, [getCount]);

  return {
    count: countData?.aggregateResourceGroup?._count?._all ?? 0,
    data: generateResourceGroupsTableData(data),
    dataTableKey,
    dataType: 'Resource Group',
    error: error || countError,
    filters,
    formName,
    headers,
    isLoading: loading || countLoading,
    isSearchable,
    offset,
    permission: EntityEnum.RESOURCE_GROUP,
    remove: useRemoveResourceGroup,
    search: (searchField: SearchField<ResourceGroupWhereInput>, searchValue: string): void => {
      setSearchParameters({
        [searchField.queryField]: searchField.transform(searchValue),
      });

      setOffset(0);
    },
    searchFields,
    setOffset,
  };
}
